import { computed, inject, Injectable, signal } from '@angular/core';
import { DOCUMENT } from '@angular/common';

export type Theme = 'light' | 'dark';

/** localStorage key holding an explicit user choice ('light' | 'dark'). */
const STORAGE_KEY = 'abx-theme';

/**
 * ThemeService — the single owner of light/dark mode for every Angular portal.
 *
 * Dark mode is driven by a `data-theme="dark"` attribute on `<html>`, which the
 * design-token layer (`libs/front/shared/design-tokens/_colors.scss` and the
 * operator's own token copy) swaps surface/text/brand tokens against. This
 * service is the only thing that sets that attribute.
 *
 * Resolution order on load: an explicit stored choice wins; otherwise the OS
 * `prefers-color-scheme` is honoured. While no explicit choice is pinned, the
 * service follows live OS changes. `toggle()`/`setTheme()` pin an explicit
 * choice and persist it.
 *
 * `providedIn: 'root'` — one instance per app, shared by every shell's toggle.
 * All DOM/storage access goes through the injected DOCUMENT and is guarded so
 * it is safe under SSR / private-mode / test environments.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly doc = inject(DOCUMENT);
  private readonly view = this.doc.defaultView;

  private readonly _theme = signal<Theme>('light');
  /** The active theme. */
  readonly theme = this._theme.asReadonly();
  /** Convenience flag — true when dark mode is active. */
  readonly isDark = computed(() => this._theme() === 'dark');

  constructor() {
    const stored = this.readStored();
    this.applyTheme(stored ?? (this.prefersDark() ? 'dark' : 'light'), false);

    // Follow the OS preference live, but only until the user pins a choice.
    this.view?.matchMedia?.('(prefers-color-scheme: dark)').addEventListener('change', event => {
      if (this.readStored() === null) {
        this.applyTheme(event.matches ? 'dark' : 'light', false);
      }
    });
  }

  /** Flip the theme and remember it as the user's explicit choice. */
  toggle(): void {
    this.applyTheme(this._theme() === 'dark' ? 'light' : 'dark', true);
  }

  /** Set a specific theme and remember it as the user's explicit choice. */
  setTheme(theme: Theme): void {
    this.applyTheme(theme, true);
  }

  private applyTheme(theme: Theme, persist: boolean): void {
    this._theme.set(theme);
    const root = this.doc.documentElement;
    if (theme === 'dark') {
      root.setAttribute('data-theme', 'dark');
    } else {
      root.removeAttribute('data-theme');
    }
    if (persist) {
      this.writeStored(theme);
    }
  }

  private prefersDark(): boolean {
    return this.view?.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
  }

  private readStored(): Theme | null {
    try {
      const value = this.view?.localStorage.getItem(STORAGE_KEY);
      return value === 'dark' || value === 'light' ? value : null;
    } catch {
      return null;
    }
  }

  private writeStored(theme: Theme): void {
    try {
      this.view?.localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      /* storage unavailable (SSR / private mode) — the in-memory signal still drives the UI */
    }
  }
}
